import { useEffect, useRef } from "react";
import {
  addFcmTokenToFirestore,
  isFcmTokenStored,
  updateFcmSyncStatus,
} from "../services/profileService";

const FCM_INSTALLATION_ID_KEY = "fcmInstallationId";
const FCM_USER_ID_KEY = "fcmUserId";

function createInstallationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `fcm-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function getOrCreateInstallationId() {
  const existing = localStorage.getItem(FCM_INSTALLATION_ID_KEY);
  if (existing) return existing;

  const next = createInstallationId();
  localStorage.setItem(FCM_INSTALLATION_ID_KEY, next);
  return next;
}

// `revalidation` is a counter the caller bumps whenever the token is re-checked
// (app foregrounded, periodic sweep). The token itself rarely changes, so a bump
// is the only signal that we should confirm the server still has it.
const useFcmTokenSync = (token, user, revalidation = 0) => {
  const lastHandledKeyRef = useRef("");
  const lastSavedBaseKeyRef = useRef("");

  useEffect(() => {
    const saveTokenIfNeeded = async () => {
      if (!user?.uid) {
        lastHandledKeyRef.current = "";
        lastSavedBaseKeyRef.current = "";
        return;
      }

      if (!token) return;

      const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const installationId = getOrCreateInstallationId();
      const baseKey = `${user.uid}:${token}:${currentTimeZone}`;
      const handledKey = `${baseKey}:${revalidation}`;

      if (lastHandledKeyRef.current === handledKey) {
        return;
      }

      lastHandledKeyRef.current = handledKey;

      try {
        // Same user, same token, same zone as our last successful write: the only
        // thing worth checking is whether the server still has it.
        if (lastSavedBaseKeyRef.current === baseKey) {
          if (await isFcmTokenStored(user.uid, token)) return;
        }

        await updateFcmSyncStatus(user.uid, {
          state: "saving",
          installationId,
          timeZone: currentTimeZone,
        });
        await addFcmTokenToFirestore(user.uid, token, { installationId });
        localStorage.setItem("fcmToken", token);
        localStorage.setItem(FCM_USER_ID_KEY, user.uid);
        localStorage.setItem("fcmEmailKey", user.email || "");
        localStorage.setItem("fcmTimeZone", currentTimeZone);
        lastSavedBaseKeyRef.current = baseKey;
        await updateFcmSyncStatus(user.uid, {
          state: "saved",
          installationId,
          timeZone: currentTimeZone,
        });
      } catch (error) {
        lastHandledKeyRef.current = "";
        lastSavedBaseKeyRef.current = "";
        updateFcmSyncStatus(user.uid, {
          state: "save_failed",
          installationId,
          timeZone: currentTimeZone,
          errorCode: error?.code || "",
          errorMessage: error?.message || String(error || "Unknown error"),
        }).catch(() => {});
        console.warn("Failed to save FCM token.", error);
      }
    };

    saveTokenIfNeeded();
  }, [token, user?.email, user?.uid, revalidation]);
};

export default useFcmTokenSync;
